import './task-widgets.scss'

import { EmailMessages, TaskAudit } from 'components'
import { AISummaryType } from 'hooks/useGetAISummary'
import useHasFeature, { AppFeatures, AppSubFeatures } from 'hooks/useHasFeature'
import React, { useEffect } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
    fetchAttachmentUrls,
    fetchAutomatedRulesAudit,
    fetchQueuedTasksAudit,
    setViewPreviousEmailMessages,
} from 'store/contactHistory/contactHistory.actions'
import { selectContactHistoryInstance } from 'store/contactHistory/contactHistory.selectors'
import { IInteraction } from 'views/Metrics/HistoricMetrics/types'
import { attachmentsRequireTransformation } from 'views/Metrics/HistoricMetrics/utils'
import AISummary from 'widgets/AISummary'
import { ContactDetails } from 'widgets/ContactDetails'

interface Props {
    interaction: IInteraction
}

const TaskWidgets: React.FC<Props> = ({ interaction }) => {
    const dispatch = useDispatch()
    const { attributeSearchName, attributeSearchValue } =
        useSelector(selectContactHistoryInstance) ?? {}

    const hasFeature = useHasFeature()

    const {
        emailMessages,
        viewPreviousEmailMessages,
        messageId,
        automatedRulesAudit,
        queuedTasksAudit,
        hasAutoReply = false,
        attributes: { 'sa-threadID': threadId },
    } = interaction

    useEffect(() => {
        batch(() => {
            const attributesArePresent = attributeSearchName && attributeSearchValue
            if (!attributesArePresent) return

            if (!automatedRulesAudit) {
                dispatch(
                    fetchAutomatedRulesAudit(
                        attributeSearchName,
                        attributeSearchValue,
                        interaction,
                    ),
                )
            }
            if (!queuedTasksAudit) {
                dispatch(
                    fetchQueuedTasksAudit(attributeSearchName, attributeSearchValue, interaction),
                )
            }
        })
    }, [interaction])

    emailMessages?.forEach(({ attachments, messageId }) => {
        if (attachmentsRequireTransformation(attachments)) {
            if (attributeSearchName && attributeSearchValue) {
                dispatch(
                    fetchAttachmentUrls(
                        attributeSearchName,
                        attributeSearchValue,
                        attachments,
                        messageId,
                        threadId,
                    ),
                )
            }
        }
    })

    return (
        <div data-testid="task-widgets">
            {hasFeature(
                AppFeatures.AI_FEATURES,
                AppSubFeatures.AI_ACTION_FLOWS_PRE_CONTACT_SUMMARIES_EMAIL,
            ) && (
                <AISummary
                    contact={interaction}
                    title="Catchup"
                    key={interaction.ID}
                    type={AISummaryType.PRE_CONTACT_EMAIL}
                />
            )}
            {emailMessages && (<EmailMessages
                emailMessages={emailMessages}
                highlightedMessageId={messageId}
                viewPreviousEmailMessages={viewPreviousEmailMessages}
                setViewPreviousEmailMessages={() =>
                    messageId && dispatch(setViewPreviousEmailMessages({ messageId, value: true }))
                }
            />)}
            <div className="contact-history-task-widgets">
                <ContactDetails contact={interaction} widgetTitle="Additional info" />
                <TaskAudit
                    automatedRulesAudit={automatedRulesAudit}
                    queuedTasksAudit={queuedTasksAudit}
                    hasAutoReply={hasAutoReply}
                />
            </div>
        </div>
    )
}

export default TaskWidgets
